/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import useScrollMemory from "../hooks/useScrollMemory";
import BioIndexExploreLevelLayout, { BioIndexTaxonCard } from "../components/BioIndexExploreLevelLayout";
import { useParams, useLocation } from "react-router-dom";
import { iconUrl, defaultImg } from "../utils/icons";
import { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db, auth } from "../firebase";


import {
  classesByPhylum,
  ordersByClass,
  familiesByOrder,
  generaByFamily,
} from "../utils/taxonomy";

export const classImages = {
  Phaeophyceae: "Sargassum.png",
  Sordariomycetes: "Hypomyces.png",
  Bryopsida: "Racomitrium.png",
  Actinopterygii: "Prionurus.png",
  Mammalia: "Saguinus.png",
  Aves: "Spizaetus.png",
  Reptilia: "Iguana.png",
  Amphibia: "Agalychnis.png",
  Appendicularia: "Bathochordaeus.png",
  Ascidiacea: "Polycarpa.png",
  Chondrichthyes: "Rhincodon.png",
  Leptocardii: "Branchistoma.png",
  Myxini: "Myxine.png",
  Petromyzontida: "Entosphenus.png",
  Sarcopterygii: "Latimeria.png",
  Thaliacea: "Pyrosoma.png",
  Arachnida: "Sphaerobothria.png",
  Chilopoda: "Scolopendra.png",
  Insecta: "Chinavia.png",
  Malacostraca: "Ocypode.png",
  Scyphozoa: "Chrysaora.png",
  Echinoidea: "Paracentrotus.png",
  Asteroidea: "Pisaster.png",
  Polyplacophora: "Chiton.png",
  Ginkgoopsida: "Ginkgo.png",
  Agaricomycetes: "Lactarius.png",
  Selaginellopsida: "Selaginella.png",
  Eudicots: "Magnolia.png",
  Monocots: "Saccharum.png",
  Jungermanniopsida: "Jungermannia.png",
  Pinopsida: "Pinus.png",
  Cycadopsida: "Cycas.png",
  Equisetopsida: "Equisetum.png",
  Pteridopsida: "Nephrolepis.png",
  Arthoniomycetes: "Cryptothecia.png",
  Lecanoromycetes: "Cladonia.png",
  Pucciniomycetes: "Hemileia.png",
  Gastropoda: "Arion.png",
};

function getClassImage(className) {
  const filename = classImages[className];
  return filename ? iconUrl(filename) : defaultImg;
}

function ClassPage() {
  useScrollMemory();

  const { kingdomName, phylumName, uid } = useParams();
  const location = useLocation();

  const currentUID = auth.currentUser?.uid;
  const dataUID = uid || currentUID;
  const classes = classesByPhylum[phylumName] || [];

  const adminModeActive =
    !!uid && new URLSearchParams(location.search).get("adminMode") === "true";

  const adminModeSuffix = adminModeActive ? "?adminMode=true" : "";

  const [speciesCounts, setSpeciesCounts] = useState({});
  const [loadingCounts, setLoadingCounts] = useState(true);

  const [browseMode, setBrowseMode] = useState("taxonomy");
  useEffect(() => {
    let cancelled = false;

    async function fetchSpeciesCounts() {
      setLoadingCounts(true);

      try {
        const snapshot = await getDocs(
          collection(db, "users", dataUID, "species"),
        );

        const counts = {};

        snapshot.forEach((doc) => {
          const data = doc.data();
          const genus = data?.genus?.toLowerCase();
          if (!genus) return;

          // 🔹 Find family
          const familyKey = Object.keys(generaByFamily).find((fam) =>
            generaByFamily[fam].map((g) => g.toLowerCase()).includes(genus),
          );
          if (!familyKey) return;

          // 🔹 Find order
          const orderKey = Object.keys(familiesByOrder).find((ord) =>
            familiesByOrder[ord]
              .map((f) => f.toLowerCase())
              .includes(familyKey.toLowerCase()),
          );
          if (!orderKey) return;

          // 🔹 Find class
          const classKey = Object.keys(ordersByClass).find((cls) =>
            ordersByClass[cls]
              .map((o) => o.toLowerCase())
              .includes(orderKey.toLowerCase()),
          );
          if (!classKey) return;

          const normalizedClass = classKey.toLowerCase();

          counts[normalizedClass] = (counts[normalizedClass] || 0) + 1;
        });

        if (!cancelled) setSpeciesCounts(counts);
      } catch (err) {
        console.error("Failed to load species counts:", err);
        if (!cancelled) setSpeciesCounts({});
      } finally {
        if (!cancelled) setLoadingCounts(false);
      }
    }

    fetchSpeciesCounts();

    return () => {
      cancelled = true;
    };
  }, [dataUID]);
  const visibleItems = classes.filter(
    (item) => Number(speciesCounts[item.toLowerCase()] || 0) > 0,
  );
  const totalSpecies = visibleItems.reduce(
    (sum, item) => sum + Number(speciesCounts[item.toLowerCase()] || 0),
    0,
  );
  const routePrefix = uid ? `/explore/${uid}` : "";

  return (
    <BioIndexExploreLevelLayout
      uid={uid}
      dataUID={dataUID}
      currentLevel="class"
      params={{ kingdomName, phylumName }}
      browseMode={browseMode}
      setBrowseMode={setBrowseMode}
      isOwnCollection={!uid || uid === currentUID}
      itemName="classes"
      itemCount={visibleItems.length}
      secondaryCount={totalSpecies}
      adminModeActive={adminModeActive}
    >
      {loadingCounts ? (
        <div className="bioindex-explore-empty" role="status">
          <span className="bioindex-explore-spinner" aria-hidden="true" />
          <h2>Loading classes</h2>
        </div>
      ) : visibleItems.length === 0 ? (
        <div className="bioindex-explore-empty">
          <h2>No classes to explore yet</h2>
          <p>There are no documented species in this group yet.</p>
        </div>
      ) : (
        <div className="bioindex-explore-kingdom-grid">
          {visibleItems.map((item) => {
            const key = item.toLowerCase();
            const count = Number(speciesCounts[key] || 0);
            const path = `/kingdom/${kingdomName}/phylum/${phylumName}/class/${key}`;
            const to = uid
              ? `${routePrefix}${path}${adminModeSuffix}`
              : path;
            return (
              <BioIndexTaxonCard
                key={item}
                name={item}
                count={count}
                image={getClassImage(item)}
                to={to}
                level="class"
                italic={false}
              />
            );
          })}
        </div>
      )}
    </BioIndexExploreLevelLayout>
  );

}

export default ClassPage;
